/**
 * Distribution Dependency Injection Container
 *
 * This container manages dependencies for the distribution feature.
 * It follows the Dependency Injection pattern for clean architecture.
 *
 * @module DistributionContainer
 */

import {DistributionApplicationService} from '../services/DistributionApplicationService';
import {IDistributionApplicationService} from '../interfaces/IDistributionApplicationService';

// Use Case Dependencies
import {GetDistributionHistoryUseCase} from '../../useCases/GetDistributionHistoryUseCase';
import {CreateDeliveryUseCase} from '../../useCases/CreateDeliveryUseCase';
import {UpdateDeliveryUseCase} from '../../useCases/UpdateDeliveryUseCase';
import {DeleteTransactionUseCase} from '../../useCases/DeleteTransactionUseCase';
import {GetMarketersForDistributionUseCase} from '../../useCases/GetMarketersForDistributionUseCase';
import {GetProductsForDistributionUseCase} from '../../useCases/GetProductsForDistributionUseCase';
import {GetAvailableSerialsUseCase} from '../../useCases/GetAvailableSerialsUseCase';
import {GetTrackedSerialsUseCase} from '../../useCases/GetTrackedSerialsUseCase';

// Repository Dependencies
import {DistributionRepositoryImpl} from '../../repository/DistributionRepositoryImpl';

export class DistributionContainer {
  private static instance: DistributionContainer;
  private services: Map<string, any> = new Map();
  private singletons: Map<string, any> = new Map();

  private constructor () {
    this.registerServices();
  }

  static getInstance (): DistributionContainer {
    if (!this.instance) {
      this.instance = new DistributionContainer();
    }
    return this.instance;
  }

  private registerServices (): void {
    // Register Repository
    this.registerSingleton('DistributionRepository', () => new DistributionRepositoryImpl());

    // Register Use Cases
    this.register('GetDistributionHistoryUseCase', () =>
      new GetDistributionHistoryUseCase(this.resolve('DistributionRepository'))
    );

    this.register('CreateDeliveryUseCase', () =>
      new CreateDeliveryUseCase(this.resolve('DistributionRepository'))
    );

    this.register('UpdateDeliveryUseCase', () =>
      new UpdateDeliveryUseCase(this.resolve('DistributionRepository'))
    );

    this.register('DeleteTransactionUseCase', () =>
      new DeleteTransactionUseCase(this.resolve('DistributionRepository'))
    );

    this.register('GetMarketersForDistributionUseCase', () =>
      new GetMarketersForDistributionUseCase(this.resolve('DistributionRepository'))
    );

    this.register('GetProductsForDistributionUseCase', () =>
      new GetProductsForDistributionUseCase(this.resolve('DistributionRepository'))
    );

    this.register('GetAvailableSerialsUseCase', () =>
      new GetAvailableSerialsUseCase(this.resolve('DistributionRepository'))
    );

    this.register('GetTrackedSerialsUseCase', () =>
      new GetTrackedSerialsUseCase(this.resolve('DistributionRepository'))
    );

    // Register Application Service
    this.registerSingleton('DistributionApplicationService', () =>
      new DistributionApplicationService(
        this.resolve('GetDistributionHistoryUseCase'),
        this.resolve('CreateDeliveryUseCase'),
        this.resolve('UpdateDeliveryUseCase'),
        this.resolve('DeleteTransactionUseCase'),
        this.resolve('GetMarketersForDistributionUseCase'),
        this.resolve('GetProductsForDistributionUseCase'),
        this.resolve('GetAvailableSerialsUseCase'),
        this.resolve('GetTrackedSerialsUseCase')
      )
    );
  }

  register<T> (key: string, factory: () => T): void {
    this.services.set(key, factory);
  }

  registerSingleton<T> (key: string, factory: () => T): void {
    this.singletons.set(key, factory);
  }

  resolve<T> (key: string): T {
    // Check singletons first
    if (this.singletons.has(key)) {
      const singleton = this.singletons.get(key);
      if (typeof singleton === 'function') {
        const instance = singleton();
        this.singletons.set(key, instance);
        return instance;
      }
      return singleton;
    }

    // Check regular services
    const factory = this.services.get(key);
    if (!factory) {
      throw new Error(`Service ${key} not found`);
    }
    return factory();
  }

  // Convenience method to get the application service
  getDistributionApplicationService (): IDistributionApplicationService {
    return this.resolve<IDistributionApplicationService>('DistributionApplicationService');
  }

  // Reset container (useful for testing)
  reset (): void {
    this.services.clear();
    this.singletons.clear();
    this.registerServices();
  }
}
